import { isValid, parse } from 'date-fns';
import { TDynamicStoreField } from '../store/store';

export type EsPasswordValidationRule = {
  id: string;
  label: string;
  isValid: boolean;
};

export type EsPasswordValidationResult = {
  rules: EsPasswordValidationRule[];
  isAllRulesValid: boolean;
  hasSimplePattern: boolean;
  hasPersonalDetails: boolean;
};

export type EsPasswordState =
  | 'unticked_requirements'
  | 'simple_pattern'
  | 'personal_details'
  | 'pattern_and_personal'
  | 'all_valid';

const ES_MIN_LENGTH = 8;
const SPECIAL_CHARACTERS_REGEX = /[!@#$%^&*(),.?":{}|<>\\/[\]`~;'\-=_+£]/;
const CONTAINS_NUMBER_REGEX = /\d/;
const CONTAINS_LETTER_REGEX = /[a-zA-Z]/;

export const hasMinLength = (password: string): boolean => password.length >= ES_MIN_LENGTH;
export const hasLetter = (password: string): boolean => CONTAINS_LETTER_REGEX.test(password);
export const hasNumber = (password: string): boolean => CONTAINS_NUMBER_REGEX.test(password);
export const hasSpecialCharacter = (password: string): boolean => SPECIAL_CHARACTERS_REGEX.test(password);

export const detectSimplePattern = (password: string): boolean => {
  if (!password || password.length < 4) return false;

  const commonSequences = [
    'abcdefghijklmnopqrstuvwxyz',
    '0123456789',
    'qwertyuiop',
    'asdfghjkl',
    'zxcvbnm',
  ];

  const normalizedStr = password.toLowerCase();

  for (const seq of commonSequences) {
    for (let i = 0; i <= seq.length - 4; i++) {
      const pattern = seq.substring(i, i + 4);
      if (normalizedStr.includes(pattern)) return true;

      const reversePattern = pattern.split('').reverse().join('');
      if (normalizedStr.includes(reversePattern)) return true;
    }
  }

  for (let i = 0; i <= normalizedStr.length - 4; i++) {
    const char = normalizedStr[i];
    let isRepetition = true;

    for (let j = 1; j < 4; j++) {
      if (normalizedStr[i + j] !== char) {
        isRepetition = false;
        break;
      }
    }

    if (isRepetition) return true;
  }

  return false;
};

export const detectPersonalDetails = (password: string, formData: TDynamicStoreField | null): boolean => {
  if (!password || password.length < 3 || !formData) return false;

  const lowercasePassword = password.toLowerCase();
  const personalInfoParts: string[] = [];

  const nameFields = ['firstName', 'middleName', 'lastName', 'secondLastName'];

  nameFields.forEach(field => {
    if (formData?.[field] && formData[field].length > 2) {
      personalInfoParts.push(formData[field].toLowerCase());

      if (formData[field].includes(' ')) {
        personalInfoParts.push(formData[field].toLowerCase().replace(/\s+/g, ''));
      }
    }
  });

  const nameParts: string[] = [];
  if (formData?.firstName) nameParts.push(formData.firstName);
  if (formData?.middleName) nameParts.push(formData.middleName);
  if (formData?.lastName) nameParts.push(formData.lastName);
  if (formData?.secondLastName) nameParts.push(formData.secondLastName);

  if (nameParts.length > 0) {
    personalInfoParts.push(nameParts.join(' ').toLowerCase());
    personalInfoParts.push(nameParts.join('').toLowerCase());
  }

  if (formData?.email && formData.email.includes('@')) {
    const emailParts = formData.email.split('@');
    const username = emailParts[0].toLowerCase();
    if (username.length > 2) {
      personalInfoParts.push(username);
    }

    const domain = `@${emailParts[1]}`.toLowerCase();
    personalInfoParts.push(domain);
    personalInfoParts.push(formData.email.toLowerCase());
  }

  if (formData?.dateOfBirth && formData.dateOfBirth.length > 5) {
    let birthDate: Date | null = null;

    const parsedDate1 = parse(formData.dateOfBirth, 'dd/MM/yyyy', new Date());
    if (isValid(parsedDate1)) {
      birthDate = parsedDate1;
    } else {
      const parsedDate2 = parse(formData.dateOfBirth, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate2)) {
        birthDate = parsedDate2;
      }
    }

    if (birthDate) {
      const day = birthDate.getDate().toString().padStart(2, '0');
      const month = (birthDate.getMonth() + 1).toString().padStart(2, '0');
      const year = birthDate.getFullYear().toString();

      const dateFormats = [
        `${day}/${month}/${year}`,
        `${day}${month}${year}`,
        `${day}.${month}.${year}`,
        `${day}-${month}-${year}`,
        `${year}.${month}.${day}`,
        `${year}${month}${day}`,
        `${year}/${month}/${day}`,
        `${year}-${month}-${day}`,
        `${month}-${day}-${year}`,
        `${month}${day}${year}`,
        `${month}.${day}.${year}`,
        `${month}/${day}/${year}`,
      ];

      dateFormats.forEach(dateFormat => {
        personalInfoParts.push(dateFormat.toLowerCase());
      });
    }
  }

  return personalInfoParts.some(part => lowercasePassword.includes(part));
};

export const validateEsPassword = (
  password: string,
  formData: TDynamicStoreField | null,
  ruleLabels: {
    minLength: string;
    hasLetter: string;
    hasNumber: string;
    hasSpecialChar: string;
  },
): EsPasswordValidationResult => {
  const rules: EsPasswordValidationRule[] = [
    {
      id:      'minLength',
      label:   ruleLabels.minLength,
      isValid: hasMinLength(password),
    },
    {
      id:      'hasLetter',
      label:   ruleLabels.hasLetter,
      isValid: hasLetter(password),
    },
    {
      id:      'hasNumber',
      label:   ruleLabels.hasNumber,
      isValid: hasNumber(password),
    },
    {
      id:      'hasSpecialChar',
      label:   ruleLabels.hasSpecialChar,
      isValid: hasSpecialCharacter(password),
    },
  ];

  const isAllRulesValid = rules.every((rule) => rule.isValid);
  const hasSimplePattern = detectSimplePattern(password);
  const hasPersonalDetails = detectPersonalDetails(password, formData);

  return {
    rules,
    isAllRulesValid,
    hasSimplePattern,
    hasPersonalDetails,
  };
};

export const getPasswordState = (
  validationResult: EsPasswordValidationResult,
  password: string,
): EsPasswordState => {
  if (!password) return 'unticked_requirements';

  const { isAllRulesValid, hasSimplePattern, hasPersonalDetails } = validationResult;

  if (hasSimplePattern && hasPersonalDetails) {
    return 'pattern_and_personal';
  }

  if (hasSimplePattern) {
    return 'simple_pattern';
  }

  if (hasPersonalDetails) {
    return 'personal_details';
  }

  if (!isAllRulesValid) {
    return 'unticked_requirements';
  }

  return 'all_valid';
};

export const isEsPasswordValid = (password: string, formData: TDynamicStoreField | null): boolean => {
  if (!password) return false;

  const isBasicValid = hasMinLength(password) &&
    hasLetter(password) &&
    hasNumber(password) &&
    hasSpecialCharacter(password);

  if (!isBasicValid) return false;

  return !detectSimplePattern(password) && !detectPersonalDetails(password, formData);
};

export default validateEsPassword;
